import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Prompt, useParams, useHistory, useLocation } from 'react-router-dom'
import { makeStyles, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import SydButton from '../../../../../commonDesign/Button'
import Icon from '../../../../../atoms/Icon'
import Loading from '../../../../../molecules/Loading'
import { smartEndpoints } from '../../../../../../api/smartEndpoints'
import AgentSidePanel from '../AgentSidePanel'
import SmartEndpointFormProvider, { useSmartEndpointForm } from './SmartEndpointFormContext'
import SmartEndpointWorkspaceProvider, { DEFAULT_ENDPOINT_CONFIG, useSmartEndpointWorkspace } from './SmartEndpointWorkspaceContext'
import SourceEditor from './SourceEditor'
import WorkSurface from './WorkSurface'

const POLICY_GROUPS = [
  {
    label: 'User Types',
    policies: [
      { id: 'authenticated_user', label: 'Any User' },
      { id: 'advisors', label: 'Advisors' },
      { id: 'wealthOwners', label: 'Wealth Owners' }
    ]
  },
  {
    label: 'Access Levels',
    policies: [
      { id: 'allAccounts', label: 'All Accounts' },
      { id: 'denali_admin', label: 'Denali Admin' },
      { id: 'admin_denali_catalog', label: 'Catalog Admin' },
      { id: 'denali_ai_enabled', label: 'AI Enabled' }
    ]
  },
  {
    label: 'Internal',
    policies: [
      { id: 'danger_zone', label: 'Danger Zone' },
      { id: 'admin_denali_internal', label: 'Denali Internal' },
      { id: 'denali_labs', label: 'Denali Labs' },
      { id: 'internalOnly', label: 'Internal Only' }
    ]
  }
]

const CURATED_POLICY_IDS = new Set(
  POLICY_GROUPS.flatMap(g => g.policies.map(p => p.id))
)

const RESERVED_POLICY_ID_SET = new Set(['allow', 'forbidden', 'smart_endpoint'])

const CONFIG_LIMITS = {
  maxReadRequests: { min: 0, max: 1000 },
  maxWriteRequests: { min: 0, max: 1000 },
  timeoutMs: { min: 1000, max: 29000 }
}

function isReservedPolicyId (policyId) {
  return RESERVED_POLICY_ID_SET.has((policyId || '').trim())
}

function getReservedPolicyIds (policyIds = []) {
  return Array.from(new Set(
    (policyIds || []).filter(policyId => isReservedPolicyId(policyId))
  ))
}

function clampConfigValue (field, value, fallback) {
  const { min, max } = CONFIG_LIMITS[field]
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(max, Math.max(min, numeric))
}

function normalizeEndpointConfig (config = {}) {
  return {
    maxReadRequests: clampConfigValue('maxReadRequests', config.maxReadRequests, DEFAULT_ENDPOINT_CONFIG.maxReadRequests),
    maxWriteRequests: clampConfigValue('maxWriteRequests', config.maxWriteRequests, DEFAULT_ENDPOINT_CONFIG.maxWriteRequests),
    timeoutMs: clampConfigValue('timeoutMs', config.timeoutMs, DEFAULT_ENDPOINT_CONFIG.timeoutMs),
    allowedPolicies: Array.isArray(config.allowedPolicies)
      ? Array.from(new Set(config.allowedPolicies.filter(p => typeof p === 'string')))
      : [...DEFAULT_ENDPOINT_CONFIG.allowedPolicies]
  }
}

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldInput: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  versionSelect: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px'
  },
  modeToggle: {
    display: 'flex',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--color-neutral-300)'
  },
  modeBtn: {
    padding: '6px 12px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'var(--color-neutral-600)',
    transition: 'all var(--transition-duration-short)',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600)'
    }
  },
  modeBtnActiveWrite: {
    backgroundColor: 'var(--color-danger-500)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--color-danger-600)'
    }
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  leftColumn: {
    flex: 1,
    minWidth: '380px'
  },
  middleColumn: {
    flex: 1,
    minWidth: '340px'
  },
  rightColumn: {
    width: '320px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  gearBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-700)'
    }
  },
  gearBtnOpen: {
    backgroundColor: 'var(--color-primary-050)',
    borderColor: 'var(--color-primary-300)',
    color: 'var(--color-primary-600)'
  },
  configPopoverAnchor: {
    position: 'relative'
  },
  configPopover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'var(--shadow-md)',
    zIndex: 200,
    padding: '14px',
    display: 'flex',
    flexDirection: 'row',
    gap: '0'
  },
  configColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  configColumnLeft: {
    width: '260px',
    paddingRight: '14px'
  },
  configColumnRight: {
    width: '180px',
    paddingLeft: '14px',
    borderLeft: '1px solid var(--color-neutral-200)'
  },
  configPopoverTitle: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-400)'
  },
  configField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  configLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--color-neutral-700)'
  },
  configInput: {
    width: '70px',
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    textAlign: 'right',
    fontFamily: 'var(--font-mono, monospace)',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  policyChipGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px'
  },
  policyChip: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'white',
    color: 'var(--color-neutral-600)',
    transition: 'all 0.1s ease',
    lineHeight: '18px',
    '&:hover': {
      borderColor: 'var(--color-primary-400)',
      color: 'var(--color-primary-600)'
    }
  },
  policyChipActive: {
    backgroundColor: 'var(--color-primary-500)',
    borderColor: 'var(--color-primary-600)',
    color: 'white',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600)',
      borderColor: 'var(--color-primary-700)',
      color: 'white'
    }
  },
  customPolicyTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px 2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-700)',
    border: '1px solid var(--color-neutral-300)',
    lineHeight: '18px',
    '& button': {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--color-neutral-400)',
      fontWeight: 600,
      fontSize: '12px',
      padding: 0,
      lineHeight: 1,
      '&:hover': {
        color: 'var(--color-danger-500)'
      }
    }
  }
}))

const serializeSaveableTests = (tests) => JSON.stringify(
  (tests || []).map(t => ({ name: t.name, testMode: t.testMode, input: t.input, responses: t.responses, assertions: t.assertions }))
)

function SmartEndpointWorkspaceInner () {
  const classes = useStyles()
  const { smartEndpointId: codeParam } = useParams()
  const history = useHistory()
  const location = useLocation()
  const isEditing = !!codeParam

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const duplicateFrom = !isEditing ? searchParams.get('duplicateFrom') : null
  const duplicateName = !isEditing ? searchParams.get('name') : null

  const form = useSmartEndpointForm()
  const workspace = useSmartEndpointWorkspace()
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [savedState, setSavedState] = useState(null)
  const configRef = useRef(null)
  const hydrated = useRef(false)
  const isDirtyRef = useRef(false)
  const duplicateHydrated = useRef(false)

  const fetchCode = isEditing ? codeParam : duplicateFrom
  const { data: programData, isLoading } = smartEndpoints.useGetProgram(
    fetchCode || null,
    isEditing ? selectedVersion : null
  )
  const saveProgram = smartEndpoints.useSaveProgram()

  useEffect(() => {
    if (!duplicateFrom || !programData || duplicateHydrated.current) return
    const { program, tests } = programData
    form.setCode('')
    form.setDisplayName(duplicateName || `${program.displayName || ''} (copy)`)
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = (tests || []).map(t => {
      const c = t.testContents || {}
      return {
        name: c.name || 'Untitled test',
        testMode: c.testMode || 'unit',
        status: null,
        lastRunMs: null,
        input: c.input || '{}',
        responses: c.responses || '[]',
        assertions: c.assertions || '',
        lastResult: null
      }
    })
    workspace.dispatchAction('loadTests', mapped)
    duplicateHydrated.current = true
  }, [duplicateFrom, duplicateName, programData, form, workspace])

  useEffect(() => {
    if (!isEditing) return
    if (!programData || hydrated.current) return
    const { program, tests } = programData
    form.setCode(program.code || '')
    form.setDisplayName(program.displayName || '')
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = (tests || []).map(t => {
      const c = t.testContents || {}
      return {
        name: c.name || 'Untitled test',
        testMode: c.testMode || 'unit',
        status: null,
        lastRunMs: null,
        input: c.input || '{}',
        responses: c.responses || '[]',
        assertions: c.assertions || '',
        lastResult: null
      }
    })
    workspace.dispatchAction('loadTests', mapped)
    setSavedState({
      source: program.source || '',
      displayName: program.displayName || '',
      description: program.description || '',
      mode,
      config: JSON.stringify(normalizedConfig),
      modifiedDate: program.modifiedDate || null,
      tests: serializeSaveableTests(mapped)
    })
    setSelectedVersion(program.version)
    hydrated.current = true
  }, [isEditing, programData, form, workspace])

  useEffect(() => {
    if (selectedVersion && hydrated.current && programData?.program?.version !== selectedVersion) {
      hydrated.current = false
    }
  }, [selectedVersion, programData])

  const versions = useMemo(() => {
    if (!programData) return selectedVersion ? [selectedVersion] : [1]
    const maxV = programData.maxVersion || programData.program.version
    return Array.from({ length: maxV }, (_, i) => i + 1)
  }, [programData, selectedVersion])

  const currentVersion = selectedVersion || (programData?.program?.version) || 1

  const handleConfigChange = useCallback((field, raw) => {
    if (!(field in CONFIG_LIMITS)) return
    const val = parseInt(raw, 10)
    if (isNaN(val)) return
    workspace.setEndpointConfig(prev => ({
      ...prev,
      [field]: clampConfigValue(field, val, prev[field])
    }))
  }, [workspace])

  const [newPolicyInput, setNewPolicyInput] = useState('')

  const handleTogglePolicy = useCallback((policyId) => {
    workspace.setEndpointConfig(prev => {
      const has = prev.allowedPolicies.includes(policyId)
      return {
        ...prev,
        allowedPolicies: has
          ? prev.allowedPolicies.filter(p => p !== policyId)
          : [...prev.allowedPolicies, policyId]
      }
    })
  }, [workspace])

  const handleAddPolicy = useCallback(() => {
    const policy = newPolicyInput.trim()
    if (!policy) return
    if (isReservedPolicyId(policy)) {
      setToast({
        message: `Policy '${policy}' is reserved and cannot be assigned to a smart endpoint.`,
        severity: 'error'
      })
      return
    }
    workspace.setEndpointConfig(prev => {
      if (prev.allowedPolicies.includes(policy)) return prev
      return { ...prev, allowedPolicies: [...prev.allowedPolicies, policy] }
    })
    setNewPolicyInput('')
  }, [newPolicyInput, workspace])

  const customPolicies = useMemo(
    () => workspace.endpointConfig.allowedPolicies.filter(p => !CURATED_POLICY_IDS.has(p)),
    [workspace.endpointConfig.allowedPolicies]
  )

  const handleSave = useCallback(async () => {
    if (!form.code || saving) return
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(form.code)) {
      setToast({ message: 'Code must be lowercase alphanumeric with hyphens', severity: 'error' })
      return
    }
    const reservedPolicies = getReservedPolicyIds(workspace.endpointConfig.allowedPolicies)
    if (reservedPolicies.length > 0) {
      setConfigOpen(true)
      setToast({
        message: `Remove reserved policies before saving: ${reservedPolicies.join(', ')}`,
        severity: 'error'
      })
      return
    }
    setSaving(true)
    try {
      const endpointConfig = normalizeEndpointConfig(workspace.endpointConfig)
      const tests = (workspace.testsRef?.current || []).map(t => ({
        name: t.name,
        testMode: t.testMode,
        input: t.input,
        responses: t.responses,
        assertions: t.assertions
      }))
      const result = await saveProgram.mutateAsync({
        programType: 'smart_endpoint',
        code: form.code,
        displayName: form.displayName,
        description: form.description,
        config: {
          ...endpointConfig,
          mode: workspace.mode
        },
        source: form.source,
        tests,
        modifiedDate: savedState?.modifiedDate || null
      })
      setSavedState({
        source: form.source,
        displayName: form.displayName,
        description: form.description,
        mode: workspace.mode,
        config: JSON.stringify(endpointConfig),
        modifiedDate: result.modifiedDate || null,
        tests: serializeSaveableTests(workspace.testsRef?.current)
      })
      setSelectedVersion(result.version)
      hydrated.current = false
      setToast({ message: result.created ? `Saved as v${result.version}` : 'Saved', severity: 'success' })
      if (!isEditing) {
        isDirtyRef.current = false
        history.replace(`/admin/denali/analysis/smart-endpoints/${form.code}`)
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save'
      const isConflict = err?.response?.status === 409
      setToast({
        message: isConflict ? 'Conflict: another user modified this endpoint. Please reload.' : msg,
        severity: 'error'
      })
    } finally {
      setSaving(false)
    }
  }, [form, workspace, saveProgram, saving, isEditing, history, savedState])

  const handleReset = useCallback(() => {
    if (!programData) {
      form.resetForm()
      workspace.setMode('query')
      workspace.setEndpointConfig(normalizeEndpointConfig(DEFAULT_ENDPOINT_CONFIG))
      workspace.dispatchAction('loadTests', [])
      return
    }
    const { program, tests } = programData
    form.setCode(program.code || '')
    form.setDisplayName(program.displayName || '')
    form.setDescription(program.description || '')
    form.setSource(program.source || '')
    const config = program.config || {}
    const mode = config.mode === 'command' ? 'command' : 'query'
    const normalizedConfig = normalizeEndpointConfig(config)
    workspace.setEndpointConfig(normalizedConfig)
    workspace.setMode(mode)
    const mapped = (tests || []).map(t => {
      const c = t.testContents || {}
      return {
        name: c.name || 'Untitled test',
        testMode: c.testMode || 'unit',
        status: null,
        lastRunMs: null,
        input: c.input || '{}',
        responses: c.responses || '[]',
        assertions: c.assertions || '',
        lastResult: null
      }
    })
    workspace.dispatchAction('loadTests', mapped)
    setSavedState({
      source: program.source || '',
      displayName: program.displayName || '',
      description: program.description || '',
      mode,
      config: JSON.stringify(normalizedConfig),
      modifiedDate: program.modifiedDate || null,
      tests: serializeSaveableTests(mapped)
    })
  }, [programData, form, workspace])

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        const agentPanel = document.querySelector('[data-agent-panel]')
        if (agentPanel && agentPanel.contains(document.activeElement)) return
        handleSave()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleSave])

  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const workspaceContext = useMemo(() => ({
    source: form.source,
    testsRef: workspace.testsRef,
    endpointConfig: workspace.endpointConfig,
    code: form.code,
    mode: workspace.mode
  }), [form.source, workspace.testsRef, workspace.endpointConfig, form.code, workspace.mode])

  const handleApplySource = useCallback((src) => {
    form.setSource(src)
  }, [form])

  const handleApplyTest = useCallback((testArgs) => {
    workspace.dispatchAction('applyTest', testArgs)
  }, [workspace])

  const codeInvalid = form.code.length > 0 && !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(form.code)

  const currentTests = workspace.testsVersion >= 0
    ? serializeSaveableTests(workspace.testsRef?.current)
    : '[]'
  const isDirty = isEditing
    ? savedState != null && (
      form.source !== savedState.source ||
      form.displayName !== savedState.displayName ||
      form.description !== savedState.description ||
      workspace.mode !== savedState.mode ||
      JSON.stringify(workspace.endpointConfig) !== savedState.config ||
      currentTests !== savedState.tests
    )
    : (
      form.hasSource ||
      form.code.length > 0 ||
      form.displayName.length > 0 ||
      form.description.length > 0 ||
      workspace.mode !== 'query' ||
      workspace.endpointConfig.maxReadRequests !== DEFAULT_ENDPOINT_CONFIG.maxReadRequests ||
      workspace.endpointConfig.maxWriteRequests !== DEFAULT_ENDPOINT_CONFIG.maxWriteRequests ||
      workspace.endpointConfig.timeoutMs !== DEFAULT_ENDPOINT_CONFIG.timeoutMs ||
      JSON.stringify(workspace.endpointConfig.allowedPolicies) !== JSON.stringify(DEFAULT_ENDPOINT_CONFIG.allowedPolicies) ||
      (workspace.testsRef?.current?.length > 0)
    )

  isDirtyRef.current = isDirty

  const lastSavedInfo = useMemo(() => {
    if (!programData?.program) return null
    const { modifiedBy, modifiedDate, createdBy, createdDate } = programData.program
    const who = modifiedBy || createdBy || null
    const when = modifiedDate || createdDate || null
    if (!when) return null
    const diff = Date.now() - new Date(when).getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(mins / 60)
    const days = Math.floor(hours / 24)
    let ago
    if (mins < 1) ago = 'just now'
    else if (mins < 60) ago = `${mins}m ago`
    else if (hours < 24) ago = `${hours}h ago`
    else ago = `${days}d ago`
    return who ? `${who}, ${ago}` : ago
  }, [programData])

  if (isLoading && ((isEditing && !hydrated.current) || (duplicateFrom && !duplicateHydrated.current))) {
    return <Loading />
  }

  return (
    <div className={classes.page}>
      <Prompt
        when={isDirty}
        message={() => {
          if (!isDirtyRef.current) return true
          return 'You have unsaved changes. Are you sure you want to leave?'
        }}
      />
      <div className={classes.headerBar}>
        <div className={classes.headerField} style={{ width: '130px' }}>
          <label className={classes.fieldLabel} style={codeInvalid ? { color: 'var(--color-danger-500)' } : undefined}>Code</label>
          <input
            className={classes.fieldInput}
            value={form.code}
            onChange={(e) => form.setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder='endpoint-code'
            readOnly={isEditing}
            style={{
              ...(isEditing ? { backgroundColor: 'var(--color-neutral-050)', cursor: 'default' } : {}),
              ...(codeInvalid ? { borderColor: 'var(--color-danger-400)', boxShadow: '0 0 0 2px var(--color-danger-100)' } : {})
            }}
          />
        </div>
        <div className={classes.headerField} style={{ width: '200px' }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.fieldInput}
            value={form.displayName}
            onChange={(e) => form.setDisplayName(e.target.value)}
            placeholder='Untitled Smart Endpoint'
          />
        </div>
        <div className={classes.headerField} style={{ flex: 1 }}>
          <label className={classes.fieldLabel}>Description</label>
          <input
            className={classes.fieldInput}
            value={form.description}
            onChange={(e) => form.setDescription(e.target.value)}
            placeholder='Add a description...'
            style={{ width: '100%' }}
          />
        </div>
        <div className={classes.headerField}>
          <label className={classes.fieldLabel}>Version</label>
          <select
            className={classes.versionSelect}
            value={currentVersion}
            onChange={(e) => {
              const v = Number(e.target.value)
              setSelectedVersion(v)
              hydrated.current = false
            }}
          >
            {versions.map(v => (
              <option key={v} value={v}>v{v}</option>
            ))}
          </select>
        </div>

        <div className={classes.headerActions}>
          <div className={classes.modeToggle} role='group' aria-label='Endpoint mode'>
            <button
              className={`${classes.modeBtn} ${workspace.mode === 'query' ? classes.modeBtnActive : ''}`}
              onClick={() => workspace.setMode('query')}
              aria-pressed={workspace.mode === 'query'}
            >
              Read Only
            </button>
            <button
              className={`${classes.modeBtn} ${workspace.mode === 'command' ? classes.modeBtnActiveWrite : ''}`}
              onClick={() => workspace.setMode('command')}
              aria-pressed={workspace.mode === 'command'}
            >
              Read/Write
            </button>
          </div>
          <div className={classes.configPopoverAnchor} ref={configRef}>
            <button
              className={`${classes.gearBtn} ${configOpen ? classes.gearBtnOpen : ''}`}
              onClick={() => setConfigOpen(prev => !prev)}
              title='Endpoint configuration'
              aria-label='Endpoint configuration'
            >
              <Icon name='settings' customSize='16px' />
            </button>
            {configOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 199 }}
                  onClick={() => setConfigOpen(false)}
                />
                <div className={classes.configPopover}>
                  <div className={`${classes.configColumn} ${classes.configColumnLeft}`}>
                    <div className={classes.configPopoverTitle}>Access Control</div>
                    {POLICY_GROUPS.map(group => (
                      <div key={group.label}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-neutral-400)', marginBottom: 3 }}>{group.label}</div>
                        <div className={classes.policyChipGroup}>
                          {group.policies.map(p => {
                            const active = workspace.endpointConfig.allowedPolicies.includes(p.id)
                            return (
                              <button
                                key={p.id}
                                className={`${classes.policyChip} ${active ? classes.policyChipActive : ''}`}
                                onClick={() => handleTogglePolicy(p.id)}
                                title={p.id}
                              >
                                {p.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                    {customPolicies.length > 0 && (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-neutral-400)', marginBottom: 3 }}>Custom</div>
                        <div className={classes.policyChipGroup}>
                          {customPolicies.map(p => (
                            <span key={p} className={classes.customPolicyTag}>
                              {p}
                              <button onClick={() => handleTogglePolicy(p)} aria-label={`Remove ${p}`}>×</button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={classes.configField} style={{ gap: 4 }}>
                      <input
                        className={classes.configInput}
                        style={{ flex: 1, width: 'auto', textAlign: 'left' }}
                        type='text'
                        placeholder='Custom policy ID'
                        value={newPolicyInput}
                        onChange={(e) => setNewPolicyInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddPolicy() }}
                      />
                      <button
                        style={{ background: 'none', border: '1px solid var(--color-neutral-300)', borderRadius: 4, cursor: 'pointer', padding: '2px 8px', fontSize: 11, color: 'var(--color-neutral-600)', whiteSpace: 'nowrap' }}
                        onClick={handleAddPolicy}
                        disabled={!newPolicyInput.trim()}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <div className={`${classes.configColumn} ${classes.configColumnRight}`}>
                    <div className={classes.configPopoverTitle}>Limits</div>
                    <div className={classes.configField}>
                      <span className={classes.configLabel}>Max reads</span>
                      <input
                        className={classes.configInput}
                        type='number'
                        min={CONFIG_LIMITS.maxReadRequests.min}
                        max={CONFIG_LIMITS.maxReadRequests.max}
                        value={workspace.endpointConfig.maxReadRequests}
                        onChange={(e) => handleConfigChange('maxReadRequests', e.target.value)}
                      />
                    </div>
                    <div className={classes.configField}>
                      <span className={classes.configLabel}>Max writes</span>
                      <input
                        className={classes.configInput}
                        type='number'
                        min={CONFIG_LIMITS.maxWriteRequests.min}
                        max={CONFIG_LIMITS.maxWriteRequests.max}
                        value={workspace.endpointConfig.maxWriteRequests}
                        onChange={(e) => handleConfigChange('maxWriteRequests', e.target.value)}
                      />
                    </div>
                    <div className={classes.configField}>
                      <span className={classes.configLabel}>Timeout</span>
                      <input
                        className={classes.configInput}
                        type='number'
                        min={CONFIG_LIMITS.timeoutMs.min}
                        max={CONFIG_LIMITS.timeoutMs.max}
                        step='1000'
                        value={workspace.endpointConfig.timeoutMs}
                        onChange={(e) => handleConfigChange('timeoutMs', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <SydButton size='sm' variant='secondary' onClick={handleReset} disabled={saving}>Reset</SydButton>
          <SydButton size='sm' variant='primary' icon='check' onClick={handleSave} disabled={!form.code || codeInvalid || saving}>
            {saving ? 'Saving\u2026' : 'Save'}
          </SydButton>
        </div>
      </div>

      <div className={classes.body}>
        <div className={`${classes.column} ${classes.leftColumn}`}>
          <SourceEditor />
        </div>
        <div className={`${classes.column} ${classes.middleColumn}`}>
          <WorkSurface />
        </div>
        <div className={`${classes.column} ${classes.rightColumn}`}>
          <AgentSidePanel
            onApplySource={handleApplySource}
            onApplyTest={handleApplyTest}
            workspaceContext={workspaceContext}
          />
        </div>
      </div>

      <div className={classes.statusBar}>
        <span>
          <span
            className={classes.statusDot}
            style={{ backgroundColor: isDirty ? 'var(--color-warning-400)' : 'var(--color-success-500)' }}
          />
          {isDirty ? 'Unsaved changes' : 'Saved'}
        </span>
        <span>Version {currentVersion}{isDirty && savedState && form.source !== savedState.source ? ' (new source)' : ''}</span>
        <span>Mode: {workspace.mode === 'query' ? 'Read Only' : 'Read/Write'}</span>
        {lastSavedInfo && (
          <span>Last saved {lastSavedInfo}</span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          Smart Endpoint &middot; {form.code || 'no code'}
        </span>
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </div>
  )
}

function SmartEndpointWorkspace () {
  return (
    <SmartEndpointFormProvider>
      <SmartEndpointWorkspaceProvider>
        <SmartEndpointWorkspaceInner />
      </SmartEndpointWorkspaceProvider>
    </SmartEndpointFormProvider>
  )
}

export default SmartEndpointWorkspace
